/**
 * Liczby z nagran zapisane po polsku: rozmiary, czas trwania, predkosc i czas do konca. Czyste funkcje bez Reacta.
 *
 * Rozmiary liczymy w jednostkach binarnych (1 GB = 1024 MB), tak jak robi to reszta formularza i jak liczy serwer
 * (limit 3 GiB pokazujemy jako "3 GB"). Jedno miejsce po przecinku, a ",0" znika: "3 GB", nie "3,0 GB".
 */

const KIB = 1024;
const MIB = KIB * 1024;
const GIB = MIB * 1024;

function liczbaPl(n: number): string {
  return n.toFixed(1).replace(/\.0$/, "").replace(".", ",");
}

/** "3 GB", "2,4 GB", "300 MB", "12,5 KB". */
export function formatujRozmiar(bajty: number): string {
  if (bajty >= GIB) return `${liczbaPl(bajty / GIB)} GB`;
  if (bajty >= MIB) return `${liczbaPl(bajty / MIB)} MB`;
  return `${liczbaPl(bajty / KIB)} KB`;
}

/** Liczba z ZADANA liczba miejsc po przecinku, z zerami na koncu ("3,00"): do porownan, gdzie ",0" mialoby znikac. */
function liczbaPlDokladnie(n: number, miejsca: number): string {
  return n.toFixed(miejsca).replace(".", ",");
}

function jednostka(bajty: number): { dzielnik: number; nazwa: string } {
  if (bajty >= GIB) return { dzielnik: GIB, nazwa: "GB" };
  if (bajty >= MIB) return { dzielnik: MIB, nazwa: "MB" };
  return { dzielnik: KIB, nazwa: "KB" };
}

function formatujSpacjami(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/**
 * Dwa rozmiary obok siebie, ktore MAJA sie roznic na ekranie, kiedy sie roznia: "Ten plik ma 3,04 GB - limit to 3,00 GB",
 * a nie "3 GB - limit to 3 GB". Zwykly format (jedno miejsce, bez ",0") wystarcza prawie zawsze; gdy po zaokragleniu
 * wychodzi ten sam napis, dokladamy miejsca po przecinku, a na koncu pokazujemy same bajty. Rowne rozmiary zostaja rowne.
 */
export function formatujRozmiaryDoPorownania(a: number, b: number): [string, string] {
  const [pa, pb] = [formatujRozmiar(a), formatujRozmiar(b)];
  if (pa !== pb || a === b) return [pa, pb];
  const [ja, jb] = [jednostka(a), jednostka(b)];
  for (const miejsca of [2, 3]) {
    const [da, db] = [
      `${liczbaPlDokladnie(a / ja.dzielnik, miejsca)} ${ja.nazwa}`,
      `${liczbaPlDokladnie(b / jb.dzielnik, miejsca)} ${jb.nazwa}`,
    ];
    if (da !== db) return [da, db];
  }
  return [`${formatujSpacjami(a)} B`, `${formatujSpacjami(b)} B`];
}

/**
 * "1,2 z 2,4 GB": obie liczby w jednostce CALOSCI, zeby pasek nie skakal miedzy MB i GB. Gdy po zaokragleniu czesc
 * wygladalaby jak calosc, a jeszcze nie jest cala ("3 z 3 GB" przy 98%), dokladamy miejsca po przecinku.
 */
export function formatujPareRozmiarow(czesc: number, calosc: number): string {
  const { dzielnik, nazwa } = jednostka(calosc);
  const a = liczbaPl(czesc / dzielnik);
  const b = liczbaPl(calosc / dzielnik);
  if (a !== b || czesc === calosc) return `${a} z ${b} ${nazwa}`;
  for (const miejsca of [2, 3]) {
    const [da, db] = [liczbaPlDokladnie(czesc / dzielnik, miejsca), liczbaPlDokladnie(calosc / dzielnik, miejsca)];
    if (da !== db) return `${da} z ${db} ${nazwa}`;
  }
  return `${formatujSpacjami(czesc)} z ${formatujSpacjami(calosc)} B`;
}

/** Czas trwania zaokraglony do minuty: "3 godz. 12 min", "2 godz.", "45 min". Ponizej minuty - sekundy. */
export function formatujCzasTrwania(sekundy: number): string {
  if (sekundy < 60) return `${Math.max(0, Math.round(sekundy))} s`;
  const minuty = Math.round(sekundy / 60);
  const godziny = Math.floor(minuty / 60);
  const reszta = minuty % 60;
  if (godziny === 0) return `${minuty} min`;
  return reszta === 0 ? `${godziny} godz.` : `${godziny} godz. ${reszta} min`;
}

/**
 * Limit czasu do zdania "do X": "10 godzin", "1 godziny", "1,5 godziny", "45 minut".
 * Dopelniacz po "do": 1 - godziny, od 2 w gore - godzin, ulamek - godziny.
 */
export function formatujLimitCzasu(sekundy: number): string {
  if (sekundy < 3600) {
    const minuty = Math.max(1, Math.round(sekundy / 60));
    return minuty === 1 ? "1 minuty" : `${minuty} minut`;
  }
  const godziny = sekundy / 3600;
  if (Number.isInteger(godziny)) return godziny === 1 ? "1 godziny" : `${godziny} godzin`;
  return `${liczbaPl(godziny)} godziny`;
}

/** "4,2 MB/s" (od 1 MB/s), nizej "850 KB/s". */
export function formatujPredkosc(bajtyNaSek: number): string {
  if (bajtyNaSek >= MIB) return `${liczbaPl(bajtyNaSek / MIB)} MB/s`;
  return `${Math.max(1, Math.round(bajtyNaSek / KIB))} KB/s`;
}

/** Czas do konca na oko: "ok. 35 min", "ok. 1 godz. 5 min", "mniej niz minuta". Nie udaje sekundowej dokladnosci. */
export function formatujPozostalyCzas(sekundy: number): string {
  if (sekundy < 60) return "mniej niż minuta";
  return `ok. ${formatujCzasTrwania(sekundy)}`;
}
